import { createSelector } from 'reselect';

import {
  getIsFromMultichoiceFromProps,
  getMatchIdFromProps,
  getPlayerIdFromProps,
  getSliderValueFromProps,
  getSportIdFromProps,
} from '../../../../selectors/_propsGetters_';
import {
  selectBasketIsCashout,
  selectBasketIsLoading,
  selectBasketItems,
  selectBasketMode,
  selectBasketStatus,
} from '../../../../selectors/basket/basketPrimary.selector';
import { selectBasketItemsMatchIds } from '../../../../selectors/basketItems.selector';
import { selectEntitiesBets } from '../../../../selectors/entities/bets/entitiesBetsPrimary.selector';
import {
  selectEntitiesMatchesEntityAvailable,
  selectEntitiesMatchesEntityIsOutright,
  selectEntitiesMatchesEntityScorers,
} from '../../../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOdds } from '../../../../selectors/entities/odds/entitiesOddsPrimary.selector';
import {
  selectEntitiesOutcomes,
  selectEntitiesOutcomesEntityAvailable,
} from '../../../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import {
  selectEntitiesPlayersEntity,
  selectEntitiesPlayersEntityMatchTimeIn,
  selectEntitiesPlayersEntityMatchTimeOut,
  selectEntitiesPlayersEntityShortname,
} from '../../../../selectors/entities/players/entitiesPlayersPrimary.selector';
import { selectMyMatchModeMatchIds } from '../../../../selectors/mymatch/mymatchPrimary.selector';
import { type BetState } from '../../../../states/entities/bets';
import { type OutcomeState } from '../../../../states/entities/outcomes';
import { type BetTemplate } from '../../../../types/bet';
import { CardTypes, type CardType } from '../../../../types/card';
import { RugbyScorerTypes } from '../../../../types/match';
import { MatchViewModes, type MatchViewMode } from '../../../../types/matchView';
import { OutcomeModes, type OutcomeMode } from '../../../../types/outcome';
import { type SelectorMaker, type Selector } from '../../../../types/selectors';
import { SportIds } from '../../../../types/sports';
import { getMinuteFromMatchTime } from '../../../../utils/date';
import OddUtils from '../../../../utils/odd';
import { getIsBasketItemSelected } from '../../../../utils/outcome';
import { createDeepEqualSelector, createShallowEqualSelector } from '../../../../utils/reselect';
import {
  makeBetMMStatusByMarketIdSelector,
  makeBetMultichoiceMMStatusByMarketIdSelector,
  makeMatchBetsBySliderValueSelector,
  makeSBVFromFilterIdSelector,
} from '../../selectors/lineUp.selector';

const emptyArray: [] = [];

type LineUpPLayerPropsType = {
  name?: string;
  nbGoals?: number;
  nbOwnGoals?: number;
  enterTimer?: string;
  leaveTimer?: string;
  cards?: Array<CardType>;
  selected?: boolean;
  canSelect: boolean;
  matchViewMode: MatchViewMode;
};

const makeLineUpPlayerPropsSelector: SelectorMaker<LineUpPLayerPropsType> = () => {
  const selectPlayerNbGoals: Selector<number | undefined> = createSelector(
    [selectEntitiesMatchesEntityScorers, getPlayerIdFromProps, getSportIdFromProps],
    (matchScorers, playerId, sportId) => {
      if (playerId) {
        if (sportId === SportIds.RUGBY || sportId === SportIds.RUGBY_LEAGUE) {
          return matchScorers
            ?.filter((scorer) => scorer.type === RugbyScorerTypes.TRY)
            ?.reduce((acc, scorer) => acc + (scorer.playerId === playerId && !scorer.ownGoal ? 1 : 0), 0);
        }
        return matchScorers?.reduce(
          (acc, scorer) => acc + (scorer.playerId === playerId && !scorer.ownGoal ? 1 : 0),
          0,
        );
      }
    },
  );

  const selectPlayerNbOwnGoals: Selector<number | undefined> = createSelector(
    [selectEntitiesMatchesEntityScorers, getPlayerIdFromProps],
    (matchScorers, playerId) =>
      matchScorers?.reduce((acc, scorer) => acc + (scorer.playerId === playerId && scorer.ownGoal ? 1 : 0), 0),
  );

  const selectPlayerEnterTimer: Selector<string | undefined> = createSelector(
    [selectEntitiesPlayersEntityMatchTimeIn],
    (matchTimeIn) => {
      if (matchTimeIn) {
        const mins = getMinuteFromMatchTime(matchTimeIn);

        if (typeof mins === 'number') {
          return `${mins}'`;
        }
      }
    },
  );

  const selectPlayerLeaveTimer: Selector<string | undefined> = createSelector(
    [selectEntitiesPlayersEntityMatchTimeOut],
    (matchTimeOut) => {
      if (matchTimeOut) {
        const mins = getMinuteFromMatchTime(matchTimeOut);

        if (typeof mins === 'number') {
          return `${mins}'`;
        }
      }
    },
  );

  const selectPlayerHasYellowCard: Selector<boolean> = createSelector(
    [selectEntitiesPlayersEntity],
    (player) => !!player?.yellowCard,
  );

  const selectPlayerHasYellowRedCard: Selector<boolean> = createSelector(
    [selectEntitiesPlayersEntity],
    (player) => !!player?.yellowRedCard,
  );

  const selectPlayerHasRedCard: Selector<boolean> = createSelector(
    [selectEntitiesPlayersEntity],
    (player) => !!player?.redCard,
  );

  const selectPlayerCards: Selector<Array<CardType>> = createSelector(
    [selectPlayerHasYellowCard, selectPlayerHasYellowRedCard, selectPlayerHasRedCard],
    (hasYellowCard, hasYellowRedCard, hasRedCard) => {
      const result: Array<CardType> = [];

      if (hasYellowCard) {
        result.push(CardTypes.YELLOW);
      }

      if (hasYellowRedCard) {
        result.push(CardTypes.YELLOW_RED);
      }

      if (hasRedCard) {
        result.push(CardTypes.RED);
      }

      return result;
    },
  );

  const selectMatchBetByMarketId: Selector<BetState | undefined> = createSelector(
    [
      makeMatchBetsBySliderValueSelector(),
      makeSBVFromFilterIdSelector(),
      getSliderValueFromProps,
      selectEntitiesOutcomes,
      getPlayerIdFromProps,
    ],
    (bets, specialBetValue, sliderValue, outcomes, playerId) =>
      bets.find((bet) =>
        sliderValue
          ? outcomes[bet.outcomes[0]]?.playerId === playerId
          : (specialBetValue && bet.specialBetValue === `${specialBetValue}`) || !specialBetValue,
      ),
  );

  const selectBetOutcomeIds: Selector<Array<number>> = createSelector(
    [selectMatchBetByMarketId],
    (bet) => bet?.outcomes ?? emptyArray,
  );

  const selectBetOutcomes: Selector<Array<OutcomeState>> = createShallowEqualSelector(
    [selectBetOutcomeIds, selectEntitiesOutcomes],
    (outcomeIds, outcomes) => outcomeIds?.map((outcomeId) => outcomes[outcomeId])?.filter((k) => k) ?? emptyArray,
  );

  const selectMatchOutcomeByPlayerId: Selector<OutcomeState | undefined> = createSelector(
    [selectBetOutcomes, getPlayerIdFromProps],
    (outcomes, playerId) => outcomes.find((outcome) => outcome.playerId === playerId),
  );

  const selectMatchOutcomeOddId: Selector<number | undefined> = createSelector(
    [selectMatchOutcomeByPlayerId],
    (outcome) => outcome?.oddId,
  );

  // TODO: replace with new joint selectors
  const selectMatchViewMode: Selector<MatchViewMode> = createSelector(
    [getMatchIdFromProps, selectMyMatchModeMatchIds],
    (matchId, myMatchModeMatchIds) =>
      matchId && myMatchModeMatchIds.indexOf(matchId) !== -1 ? MatchViewModes.MY_MATCH : MatchViewModes.CLASSIC,
  );

  const selectLineUpIsPlayerItemSelected: Selector<boolean> = createSelector(
    [
      selectBasketMode,
      selectBasketStatus,
      selectBasketIsLoading,
      selectBasketItems,
      selectMatchOutcomeOddId,
      selectMatchViewMode,
      getIsFromMultichoiceFromProps,
      makeBetMMStatusByMarketIdSelector(),
      makeBetMultichoiceMMStatusByMarketIdSelector(),
    ],
    (
      basketMode,
      basketStatus,
      isLoading,
      items,
      oddsId,
      matchViewMode,
      isFromMultichoice,
      betMMStatus,
      betMultichoiceMMStatus,
    ) =>
      oddsId
        ? getIsBasketItemSelected(
            basketMode,
            basketStatus,
            isLoading,
            oddsId,
            items,
            matchViewMode,
            isFromMultichoice,
            betMMStatus,
            betMultichoiceMMStatus,
          )
        : false,
  );

  const selectBetBetId: Selector<number | undefined> = createSelector([selectMatchBetByMarketId], (bet) => bet?.betId);

  const selectBetTemplate: Selector<BetTemplate | null> = createSelector(
    [selectMatchBetByMarketId],
    (bet) => bet?.template ?? null,
  );

  const selectOdd: Selector<number | null> = createSelector(
    [selectEntitiesOdds, selectMatchOutcomeOddId],
    (odds, oddId) => (oddId ? odds?.[oddId] : null),
  );

  const selectIsAllowed: Selector<boolean> = createSelector(
    [
      selectLineUpIsPlayerItemSelected,
      selectBasketIsCashout,
      selectBasketItemsMatchIds,
      getMatchIdFromProps,
      selectEntitiesMatchesEntityIsOutright,
    ],
    (selected, isCashout, basketItemsMatchIds, matchId, isOutright) =>
      selected || !isCashout || (matchId && basketItemsMatchIds.indexOf(matchId) === -1) || isOutright,
  );

  const selectBetIsAvailable: Selector<boolean> = createSelector([selectBetBetId, selectEntitiesBets], (betId, bets) =>
    betId ? bets[betId]?.available ?? false : false,
  );

  // TODO: replace with the new match selector
  const selectPlayerMode: Selector<OutcomeMode> = createSelector(
    [
      selectEntitiesMatchesEntityAvailable,
      selectBetIsAvailable,
      selectBetTemplate,
      selectOdd,
      selectEntitiesOutcomesEntityAvailable,
      selectIsAllowed,
    ],
    (matchIsAvailable, betIsAvailable, betTemplate, oddValue, outcomeIsAvailable, allowed) =>
      OddUtils.getMode({
        matchIsAvailable,
        betIsAvailable,
        betTemplate,
        oddValue,
        outcomeIsAvailable,
        // @ts-ignore
        allowed,
      }),
  );

  return createDeepEqualSelector(
    [
      selectEntitiesPlayersEntityShortname,
      selectPlayerNbGoals,
      selectPlayerNbOwnGoals,
      selectPlayerEnterTimer,
      selectPlayerLeaveTimer,
      selectPlayerCards,
      selectLineUpIsPlayerItemSelected,
      selectPlayerMode,
      selectMatchViewMode,
    ],
    (name, nbGoals, nbOwnGoals, enterTimer, leaveTimer, cards, selected, mode, matchViewMode) => ({
      name,
      nbGoals,
      nbOwnGoals,
      enterTimer,
      leaveTimer,
      cards,
      selected,
      canSelect: mode === OutcomeModes.PLAY,
      matchViewMode,
    }),
  );
};

export default makeLineUpPlayerPropsSelector;
